import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaExchangeAlt, FaCalculator } from 'react-icons/fa';
import { useCurrency } from '../context/CurrencyContext';

const AssetComparison = ({ asset, onClose }) => {
    const { formatPrice } = useCurrency();
    const [compareCurrency, setCompareCurrency] = useState('USD');
    const [historicalRate, setHistoricalRate] = useState('');
    const [result, setResult] = useState(null);
    const [loadingRate, setLoadingRate] = useState(false);
    const [rateError, setRateError] = useState(null);
    const [allCurrencies, setAllCurrencies] = useState([]);

    useEffect(() => {
        axios.get('/api/currency')
            .then(res => setAllCurrencies(res.data))
            .catch(err => console.error(err));
    }, []);

    // Fetch historical rate when currency or asset changes
    useEffect(() => {
        if (!asset.buyDate || !compareCurrency) return;

        const fetchHistoricalRate = async () => {
            setLoadingRate(true);
            setRateError(null);
            setHistoricalRate(''); // Reset

            try {
                // Dates are normally in ISO object in asset, ensuring format YYYY-MM-DD
                const date = new Date(asset.buyDate).toISOString().split('T')[0];
                const res = await axios.get(`/api/currency/historical-rate/check?date=${date}&code=${compareCurrency}`);

                if (res.data.rate) {
                    setHistoricalRate(res.data.rate);
                } else {
                    // Not found or not supported (Gold etc)
                    setRateError('Otomatik kur bulunamadı, lütfen manuel giriniz.');
                }
            } catch (error) {
                console.error('Historical rate fetch error:', error);
                setRateError('Kur getirilemedi, manuel giriniz.');
            } finally {
                setLoadingRate(false);
            }
        };

        fetchHistoricalRate();
    }, [asset.buyDate, compareCurrency]);

    const assetCode = asset.type;

    // Filter out the current asset type from options
    const availableOptions = [
        { code: 'USD', name: 'Amerikan Doları' },
        { code: 'EUR', name: 'Euro' },
        { code: 'GBP', name: 'İngiliz Sterlini' },
        { code: 'XAUTRYG', name: 'Gram Altın' },
        { code: 'XAG=X', name: 'Gümüş (Ons)' },
        { code: 'BTC-USD', name: 'Bitcoin (Mock)' }
    ].filter(c => c.code !== assetCode);

    const handleCompare = (e) => {
        e.preventDefault();
        if (!historicalRate || parseFloat(historicalRate) <= 0) return;

        // 1. Calculate how much of B we could buy
        // Cost of A was: asset.totalCost
        const amountOfB = asset.totalCost / parseFloat(historicalRate);

        // 2. Find current price of B
        // We need current rates. allCurrencies passed from parent? Or context?
        // Let's assume we can find it in allCurrencies or fetch it.
        // Ideally parent passes list of current rates/currencies.
        // For now let's assume `allCurrencies` is passed as prop.
        const currentCurrencyB = allCurrencies?.find(c => c.code === compareCurrency);
        const currentPriceB = currentCurrencyB ? currentCurrencyB.sellPrice : 0;

        // 3. Current Value of B
        const currentValueB = amountOfB * currentPriceB;

        // 4. Difference
        const diff = currentValueB - asset.currentValue;

        setResult({
            amountOfB,
            currentValueB,
            diff,
            percentDiff: ((currentValueB - asset.currentValue) / asset.currentValue) * 100
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[calc(100vh-2rem)] overflow-y-auto p-4 sm:p-6 relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                    <FaTimes size={20} />
                </button>

                <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
                    <FaExchangeAlt className="text-blue-500" />
                    Karşılaştırma Analizi
                </h2>

                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6">
                    <div className="font-semibold text-blue-800 dark:text-blue-300">Mevcut Varlık:</div>
                    <div className="text-lg">{asset.amount} adet {asset.type}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Alış: {new Date(asset.buyDate).toLocaleDateString('tr-TR')} - Maliyet: {formatPrice(asset.totalCost)}
                    </div>
                    <div className="mt-2 font-bold text-gray-800 dark:text-white">
                        Şu anki Değer: {formatPrice(asset.currentValue)}
                    </div>
                </div>

                <form onSubmit={handleCompare} className="space-y-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Bunun yerine ne alsaydım?
                        </label>
                        <select
                            value={compareCurrency}
                            onChange={(e) => setCompareCurrency(e.target.value)}
                            className="w-full p-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                            {availableOptions.map(opt => (
                                <option key={opt.code} value={opt.code}>{opt.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            O tarihteki {compareCurrency} kuru (TL):
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                step="0.0001"
                                required
                                value={historicalRate}
                                onChange={(e) => setHistoricalRate(e.target.value)}
                                className={`w-full p-2 rounded border dark:bg-gray-700 dark:border-gray-600 dark:text-white ${loadingRate ? 'opacity-50' : ''}`}
                                placeholder={loadingRate ? 'Kur aranıyor...' : 'Örn: 18.45'}
                                disabled={loadingRate}
                            />
                            {loadingRate && (
                                <div className="absolute right-3 top-2.5 animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
                            )}
                        </div>

                        {rateError ? (
                            <p className="text-xs text-orange-500 mt-1">{rateError}</p>
                        ) : (
                            <p className="text-xs text-green-600 mt-1 dark:text-green-400">
                                {historicalRate && !loadingRate && '✓ Otomatik kur getirildi'}
                            </p>
                        )}
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded transition flex items-center justify-center gap-2"
                    >
                        <FaCalculator /> Hesapla
                    </button>
                </form>

                {result && (
                    <div className={`p-4 rounded-lg border-l-4 ${result.diff > 0 ? 'bg-green-50 border-green-500 dark:bg-green-900/20' : 'bg-red-50 border-red-500 dark:bg-red-900/20'}`}>
                        <h3 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">Sonuç:</h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-1">
                            Eğer o tarihte <strong>{compareCurrency}</strong> alsaydınız:
                        </p>
                        <p className="text-xl font-bold mb-2 text-gray-900 dark:text-white">
                            {result.amountOfB.toFixed(2)} {compareCurrency} = {formatPrice(result.currentValueB)}
                        </p>
                        <hr className="border-gray-200 dark:border-gray-700 my-2" />
                        <div className="flex justify-between items-center">
                            <span className="font-medium">Fark:</span>
                            <span className={`text-lg font-bold ${result.diff > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {result.diff > 0 ? '+' : ''}{formatPrice(result.diff)} ({result.diff > 0 ? '+' : ''}{result.percentDiff.toFixed(2)}%)
                            </span>
                        </div>
                        <p className="text-xs mt-2 text-gray-500 italic">
                            {result.diff > 0
                                ? `${compareCurrency} almak daha karlı olurdu.`
                                : `Mevcut tercihiniz (${asset.type}) daha iyi performans göstermiş.`}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssetComparison;
